import LayoutContainer from 'components/Common/LayoutContainer'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import FlightCampaignsDeals from './FlightCampaignsDeals'
import FlightCampaignsAllDeals from './FlightCampaignsAllDeals'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { dateDifference } from 'lib/datetime/dateUtils'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'

const FlightDealHero = styled.div`
  position: relative;
  background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
  padding: ${rem(48)} 0;

  ${mediaQueryUp.desktop} {
    padding: ${rem(64)} 0;
  }
`

const HeroImage = styled(ResponsiveImage)`
  position: absolute;
  inset: 0;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
    pointer-events: none;
  }
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.7);
`

const CenteredGroup = styled(Group)`
  text-align: center;
`

interface Props {
  campaign: App.FlightCampaign
}

export default function FlightCampaignsLandingPageContent(props: Props) {
  const { campaign } = props
  const intl = useIntl()

  const showAllDeals = campaign.type === 'all'

  const endDateLabel = useMemo(() => {
    const difference = dateDifference(new Date(campaign.end_date))

    if (difference.days <= 1) {
      return intl.formatMessage(
        {
          id: 'WXMUG_effQDiapw5xHaO',
          defaultMessage: 'OFFER ENDS IN {hours}H : {minutes}M : {seconds}S',
          description: 'Flight campaign urgency label showing countdown in hours, minutes, and seconds',
        },
        {
          hours: Math.max(difference.days * 24 + difference.hours, 0),
          minutes: Math.max(difference.minutes, 0),
          seconds: Math.max(difference.seconds, 0),
        },
      )
    }

    return intl.formatMessage(
      {
        id: 'AP1M075JOHANwNsT6_Ey',
        defaultMessage: 'OFFER ENDS IN {days, plural, one {# DAY} other {# DAYS}}',
        description: 'Flight campaign urgency label showing number of days until offer ends',
      },
      { days: difference.days },
    )
  }, [campaign.end_date, intl])

  return <VerticalSpacer gap={48}>
    <FlightDealHero style={{ '--search-offset': '0rem' } as any}>
      <HeroImage
        id={campaign.hero_image_public_id}
        fit="center"
      />

      <Overlay />

      <LayoutContainer style={{ transform: 'translateZ(0)' }}>
        <CenteredGroup direction="vertical" horizontalAlign="center" verticalAlign="center">
          <VerticalSpacer gap={12}>
            <HeadingTextBlock variant="heading1" colour="dark-contrast" align="center">
              {campaign.title}
            </HeadingTextBlock>

            <BodyTextBlock variant="large" colour="dark-contrast" align="center">
              {campaign.header}
            </BodyTextBlock>

            {
              campaign.type !== 'all' && (
                <UrgencyLabel variant="speed">
                  {endDateLabel}
                </UrgencyLabel>
              )
            }
          </VerticalSpacer>
        </CenteredGroup>
      </LayoutContainer>
    </FlightDealHero>

    <LayoutContainer>
      {!showAllDeals && <FlightCampaignsDeals campaign={campaign} />}
      {showAllDeals && <FlightCampaignsAllDeals />}
    </LayoutContainer>
  </VerticalSpacer>
}
